
import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, updateDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { deleteUser } from 'firebase/auth';
import { auth } from '../firebase';
import { Card, Button, Badge } from '../components/ui';

const TOPIC_OPTIONS = [
  { id: 'court', label: 'Family Court Process', icon: '🏛️' },
  { id: 'alienation', label: 'Parental Alienation', icon: '💔' },
  { id: 'contact', label: 'Blocked Contact', icon: '🚫' },
  { id: 'safeguarding', label: 'Safeguarding & SS', icon: '🚩' },
  { id: 'mediation', label: 'Mediation / MIAM', icon: '🤝' },
  { id: 'finances', label: 'Finances & Maintenance', icon: '💰' },
];

const Settings = () => {
  const { userProfile, refreshProfile } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  
  // Buddy profile state
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [bio, setBio] = useState('');
  const [capacity, setCapacity] = useState(5);
  const [isSavingBuddy, setIsSavingBuddy] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
     if (userProfile?.isBuddy && userProfile?.buddyProfile) {
         setSelectedTopics(userProfile.buddyProfile.topics || []);
         setBio(userProfile.buddyProfile.bio || '');
         setCapacity(userProfile.buddyProfile.capacity || 5);
     }
  }, [userProfile]);

  const toggleOnlineStatus = async () => {
    if (!userProfile) return;
    setIsSaving(true);
    try {
      const userRef = doc(db, 'users', userProfile.uid);
      await updateDoc(userRef, {
        isOnlineHidden: !userProfile.isOnlineHidden,
        lastUpdated: serverTimestamp()
      });
      await refreshProfile();
    } catch (err) {
      console.error("Failed to update settings:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleTopic = (topicId: string) => {
      setSelectedTopics(prev => 
          prev.includes(topicId) ? prev.filter(t => t !== topicId) : [...prev, topicId]
      );
  };

  const handleSaveBuddyProfile = (e: React.FormEvent) => {
      e.preventDefault();
      if (selectedTopics.length === 0) {
          alert("Please select at least one area of experience.");
          return;
      }
      if (!bio.trim()) {
          alert("Please provide a short bio.");
          return;
      }

      setShowDisclaimer(true);
  };

  const confirmAndSaveBuddyProfile = async () => {
      setIsSavingBuddy(true);
      setShowDisclaimer(false);
      try {
          const userRef = doc(db, 'users', userProfile!.uid);
          await updateDoc(userRef, {
              'buddyProfile.topics': selectedTopics,
              'buddyProfile.bio': bio.trim(),
              'buddyProfile.capacity': capacity,
              'buddyProfile.status': 'active'
          });
          await refreshProfile();
          alert("Buddy Profile updated successfully!");
      } catch (error) {
          console.error("Error updating buddy profile:", error);
          alert("Failed to save profile. Please try again.");
      } finally {
        setIsSavingBuddy(false);
      }
  };

  const handleDeleteAccount = async () => {
    if (!auth.currentUser || !userProfile) return;
    setIsDeletingAccount(true);
    try {
      // 1. Delete Firestore Document
      await deleteDoc(doc(db, 'users', userProfile.uid));
      
      // 2. Delete Auth User
      await deleteUser(auth.currentUser);
      
      // The auth state listener will handle the redirect
    } catch (error: any) {
      console.error("Error deleting account:", error);
      if (error.code === 'auth/requires-recent-login') {
        alert("For security reasons, you must log out and log back in before deleting your account.");
      } else {
        alert("Failed to delete account. Please contact support.");
      }
    } finally {
      setIsDeletingAccount(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <Layout>
      {showDisclaimer && (
          <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
              <Card className="max-w-md w-full p-6 shadow-2xl relative">
                  <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <span>🤝</span> Buddy Agreement
                  </h3>
                  <div className="space-y-4 text-sm text-slate-600 mb-6">
                      <p>Before activating your profile, please confirm you understand the boundaries of the Dads Matter Buddy role.</p>
                      
                      <div className="bg-emerald-50 border border-emerald-100 p-3 rounded-lg">
                          <p className="font-bold text-emerald-800 mb-2">✅ YOU CAN:</p>
                          <ul className="list-disc pl-5 space-y-1 text-emerald-700">
                              <li>Share your lived experience and what worked for you.</li>
                              <li>Offer emotional support and a listening ear.</li>
                              <li>Signpost to professional resources.</li>
                          </ul>
                      </div>

                      <div className="bg-rose-50 border border-rose-100 p-3 rounded-lg">
                          <p className="font-bold text-rose-800 mb-2">❌ YOU CANNOT:</p>
                          <ul className="list-disc pl-5 space-y-1 text-rose-700">
                              <li>Give direct legal advice or act as a solicitor.</li>
                              <li>Offer clinical or medical advice.</li>
                              <li>Guarantee specific outcomes in their cases.</li>
                          </ul>
                      </div>
                  </div>
                  
                  <div className="flex gap-3 justify-end">
                      <Button variant="outline" onClick={() => setShowDisclaimer(false)} disabled={isSavingBuddy}>Cancel</Button>
                      <Button onClick={confirmAndSaveBuddyProfile} disabled={isSavingBuddy} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md">I Agree & Save Profile</Button>
                  </div>
              </Card>
          </div>
      )}

      {showDeleteConfirm && (
          <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
              <Card className="max-w-md w-full p-6 shadow-2xl relative border-rose-200">
                  <div className="w-12 h-12 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center text-2xl mb-4">
                      ⚠️
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                      Delete Your Account?
                  </h3>
                  <div className="space-y-4 text-sm text-slate-600 mb-6">
                      <p>This action is <strong>permanent and cannot be undone</strong>.</p>
                      <p>Deleting your account will permanently remove:</p>
                      <ul className="list-disc pl-5 space-y-1 text-slate-700">
                          <li>Your profile and login credentials</li>
                          <li>Your training progress and certificates</li>
                          <li>All your forum posts and direct messages</li>
                          <li>Your active subscription (if any) will be canceled</li>
                      </ul>
                  </div>
                  
                  <div className="flex gap-3 justify-end">
                      <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={isDeletingAccount}>Cancel</Button>
                      <Button onClick={handleDeleteAccount} disabled={isDeletingAccount} className="bg-rose-600 hover:bg-rose-700 text-white shadow-md">
                          {isDeletingAccount ? 'Deleting...' : 'Yes, Delete My Account'}
                      </Button>
                  </div>
              </Card>
          </div>
      )}

      <div className="max-w-2xl mx-auto py-8 px-4 animate-slide-up">
        <div className="mb-8">
            <h2 className="text-3xl font-heading font-bold text-slate-900">Settings</h2>
            <p className="text-slate-500">Manage your profile and privacy preferences.</p>
        </div>

        <div className="space-y-6">
            <Card className="p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <span>🛡️</span> Privacy Control
                </h3>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="flex-1 pr-4">
                        <p className="text-sm font-bold text-slate-700">Hide Online Status</p>
                        <p className="text-xs text-slate-500 mt-1">When enabled, other users won't see a green indicator when you are active in the Hub.</p>
                    </div>
                    <button 
                        onClick={toggleOnlineStatus}
                        disabled={isSaving}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${userProfile?.isOnlineHidden ? 'bg-indigo-600' : 'bg-slate-300'}`}
                    >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${userProfile?.isOnlineHidden ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                </div>
            </Card>

            <Card className="p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <span>👤</span> Identity & Account
                </h3>
                <div className="space-y-6">
                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Hub Nickname (Public)</p>
                            <span className="text-[9px] font-bold text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded">VISIBLE TO OTHERS</span>
                        </div>
                        <p className="text-sm font-bold text-slate-900 bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center gap-2">
                           <span className="text-indigo-400">@</span>{userProfile?.nickname || 'Not Set'}
                        </p>
                        <p className="text-[10px] text-slate-500 mt-1.5 px-1 italic">This is your anonymous identity used in the Dads Hub and Direct Messages.</p>
                    </div>

                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Registered Name (Private)</p>
                            <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">PRIVATE RECORDS</span>
                        </div>
                        <p className="text-sm font-medium text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100">{userProfile?.displayName}</p>
                        <p className="text-[10px] text-slate-400 mt-1.5 px-1">This is your legal name used for <strong>Certificates of Completion</strong> and internal billing records only. It is never shown to other members.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Email Address</p>
                            <p className="text-xs font-medium text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100 truncate">{userProfile?.email}</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Account Role</p>
                            <div className="mt-1"><Badge color="blue">{userProfile?.role.toUpperCase()}</Badge></div>
                        </div>
                    </div>
                </div>
            </Card>

            {userProfile?.isBuddy && (
                <Card className={`p-6 border-2 ${userProfile.buddyProfile?.status === 'pending' ? 'border-amber-400 bg-amber-50/30' : 'border-slate-200'}`}>
                    <div className="flex justify-between items-start mb-6">
                        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            <span>🤝</span> Dads Matter Buddy Profile
                        </h3>
                        {userProfile.buddyProfile?.status === 'pending' ? (
                            <Badge color="yellow">SETUP REQUIRED</Badge>
                        ) : (
                            <Badge color="green">ACTIVE BUDDY</Badge>
                        )}
                    </div>
                    
                    {userProfile.buddyProfile?.status === 'pending' && (
                        <p className="text-sm text-slate-600 mb-6 bg-white p-4 rounded-xl border border-amber-200 shadow-sm">
                            <strong>Welcome!</strong> Please complete your profile below to start accepting mentorship connections from dads in need.
                        </p>
                    )}

                    <form onSubmit={handleSaveBuddyProfile} className="space-y-8">
                        <div>
                            <h3 className="text-sm font-bold text-slate-900 mb-2">1. Your Areas of Experience</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {TOPIC_OPTIONS.map(topic => (
                                    <div 
                                        key={topic.id}
                                        onClick={() => toggleTopic(topic.id)}
                                        className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center gap-2 ${selectedTopics.includes(topic.id) ? 'border-amber-500 bg-amber-50 shadow-sm' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                                    >
                                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${selectedTopics.includes(topic.id) ? 'border-amber-500 bg-amber-500 text-white' : 'border-slate-300'}`}>
                                            {selectedTopics.includes(topic.id) && <span className="text-[10px]">✓</span>}
                                        </div>
                                        <span>{topic.icon}</span>
                                        <span className={`font-bold text-xs ${selectedTopics.includes(topic.id) ? 'text-amber-900' : 'text-slate-700'}`}>{topic.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-sm font-bold text-slate-900 mb-2">2. Short Bio</h3>
                            <textarea 
                                value={bio}
                                onChange={e => setBio(e.target.value)}
                                placeholder="e.g. Hi, I'm Dave. I spent 2 years in the family courts..."
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-indigo-500 h-[100px]"
                                required
                            />
                        </div>

                        <div>
                            <h3 className="text-sm font-bold text-slate-900 mb-2">3. Mentorship Capacity</h3>
                            <p className="text-xs text-slate-500 mb-3">How many dads can you comfortably support at one time?</p>
                            <div className="flex items-center gap-4">
                                <input 
                                    type="range" 
                                    min="1" 
                                    max="10" 
                                    value={capacity} 
                                    onChange={e => setCapacity(parseInt(e.target.value))}
                                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
                                />
                                <div className="min-w-[3rem] text-center bg-amber-50 text-amber-900 font-bold px-2 py-1.5 rounded-lg border border-amber-200 text-sm">
                                    {capacity}
                                </div>
                            </div>
                        </div>

                        <div className="pt-2 border-t border-slate-100 flex justify-end">
                            <Button type="submit" disabled={isSavingBuddy} className="bg-slate-900 hover:bg-slate-800 text-white shadow-md">
                                {isSavingBuddy ? 'Saving...' : 'Save Buddy Profile'}
                            </Button>
                        </div>
                    </form>
                </Card>
            )}

            <Card className="p-6 border-rose-100 bg-rose-50/30">
                <h3 className="text-lg font-bold text-rose-800 mb-2 flex items-center gap-2">
                    <span>⚠️</span> Danger Zone
                </h3>
                <p className="text-sm text-rose-600 mb-4">
                    Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <Button 
                    variant="outline" 
                    onClick={() => setShowDeleteConfirm(true)}
                    className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:border-rose-300"
                >
                    Delete Account
                </Button>
            </Card>

            <div className="text-center pt-6">
                <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">Dads Matter Training Hub v1.0.5</p>
            </div>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
